import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fetchServicesPage,
  findSection,
  SERVICES_SECTION_KEYS,
} from "../services/contentApi";

const ServicesPageContext = createContext(null);

export function ServicesPageProvider({ children }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchServicesPage()
      .then((data) => {
        if (!cancelled) {
          setPage(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setPage(null);
        }
        console.error("Error fetching services page:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(() => {
    const result = {};
    SERVICES_SECTION_KEYS.forEach((key) => {
      result[key] = findSection(page, key);
    });
    return result;
  }, [page]);

  const value = useMemo(
    () => ({ page, sections, loading, error }),
    [page, sections, loading, error]
  );

  return (
    <ServicesPageContext.Provider value={value}>
      {children}
    </ServicesPageContext.Provider>
  );
}

export function useServicesPage() {
  const ctx = useContext(ServicesPageContext);
  if (!ctx) {
    throw new Error("useServicesPage must be used within ServicesPageProvider");
  }
  return ctx;
}

export function useServicesSection(key) {
  const ctx = useContext(ServicesPageContext);
  if (!ctx) {
    return { section: null, loading: false, fromServicesPage: false };
  }
  return {
    section: ctx.sections[key] ?? null,
    loading: ctx.loading,
    fromServicesPage: true,
  };
}
